/**
 * Shared hook for From / CC / BCC options across scheduling steps.
 *
 * Scheduling context (adhoc + manual) deliberately excludes the
 * `organizerSender` sentinel — that placeholder only makes sense on
 * the interview-plan template (where "whoever schedules" is unknown).
 * On the actual interview we know who's scheduling, so the From list
 * shows real delegatable users and CC/BCC mirrors the same real-user
 * set. The `useInterviewCommunicationSenderOptions` API already
 * returns the delegatable set; we just drop the sentinel in case it
 * leaks through and return the rest verbatim.
 *
 * Shared mailboxes (`feSharedEmails` flag) are appended to
 * `fromOptions` with a `shared:${id}` value prefix so consumers can
 * tell them apart from regular user options at save time. CC/BCC
 * keep regular users only — backend doesn't accept shared-mailbox
 * recipients on those fields.
 */
import { useMemo } from 'react';
import type { SelectOption } from '@kula-ai/components';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  asyncSelectProps,
  mergeOptionsByValue,
  useCcBccUserOptions,
  useSenderAutocompleteProps,
} from '@/api/userSearch/useCcBccUserOptions';
import { useSharedMailboxes } from '@/api/sharedMailboxes';
import { useInterviewCommunicationSenderOptions } from '../../InterviewActivity/communication/api';
import { INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE } from '../../InterviewActivity/communication/types';

/**
 * Form-value prefix marking a SelectOption as a shared mailbox.
 * `useScheduleSenderOptions` emits `shared:${id}`; consumers
 * (validators + payload mappers) detect it via this prefix to route
 * the picked id to `candidate_mail_ats_shared_email_id` instead of
 * the user-id `candidate_mail_from` field.
 */
export const SHARED_EMAIL_VALUE_PREFIX = 'shared:';

export interface UseScheduleSenderOptionsArgs {
  /** Append shared-mailbox options to `fromOptions`. */
  includeSharedMailboxes?: boolean;
  /** Saved cc/bcc participants, so chips outside the first page still label. */
  knownCcBccOptions?: SelectOption[];
  /** Saved senders, so a From outside the delegatable list still labels. */
  knownFromOptions?: SelectOption[];
}

export function useScheduleSenderOptions(
  args: UseScheduleSenderOptionsArgs = {}
) {
  const {
    includeSharedMailboxes = false,
    knownCcBccOptions,
    knownFromOptions,
  } = args;
  const {
    data: delegatableRaw = [],
    isLoading,
    isFetched,
  } = useInterviewCommunicationSenderOptions();
  const { feSharedEmails } = useKFlags();
  const { sharedMailboxes } = useSharedMailboxes();

  const realUserOptions = useMemo(
    () =>
      delegatableRaw.filter(
        (o) => o.value !== INTERVIEW_EMAIL_SENDER_ORGANIZER_VALUE
      ),
    [delegatableRaw]
  );

  const sharedEmailOptions = useMemo<SelectOption[]>(() => {
    if (!includeSharedMailboxes || !feSharedEmails) return [];
    return sharedMailboxes
      .filter(
        (row) =>
          typeof row?.id === 'number' && Number.isFinite(row.id) && row.id > 0
      )
      .map((row) => ({
        value: `${SHARED_EMAIL_VALUE_PREFIX}${row.id}`,
        // Name as the visible label, email as the secondary line —
        // matches the real-user rows. Fall back to email when the
        // name is missing (defensive — the API normally populates both).
        label: row.name ?? row.email ?? `Shared mailbox ${row.id}`,
        description: row.name && row.email ? row.email : undefined,
        image: undefined,
      }));
  }, [includeSharedMailboxes, feSharedEmails, sharedMailboxes]);

  // Shared mailboxes go AFTER regular users so the typical case
  // (pick a teammate) is in the leading visual slot. Recruiters
  // mostly send as themselves or another scheduler.
  // The saved sender leads: it may sit outside the delegatable list entirely.
  const fromOptions = useMemo(
    () =>
      mergeOptionsByValue(
        knownFromOptions ?? [],
        realUserOptions,
        sharedEmailOptions
      ),
    [knownFromOptions, realUserOptions, sharedEmailOptions]
  );

  // ENG-3766: CC/BCC is every active user, not the delegatable senders.
  const {
    initialOptions: ccBccOptions,
    loadOptions: loadCcBccOptions,
    getEmailByValue: getCcBccEmailByValue,
  } = useCcBccUserOptions(knownCcBccOptions);
  const ccBccSelectProps = useMemo(
    () => asyncSelectProps(loadCcBccOptions),
    [loadCcBccOptions]
  );

  const fromSelectProps = useSenderAutocompleteProps(fromOptions);

  // Senders the account can actually send as, WITHOUT `knownFromOptions`.
  // `fromOptions` carries the saved sender so the picker can label them even
  // once they stop being delegatable — which makes it the wrong set to
  // validate against (ENG-3916).
  const validFromValues = useMemo(
    () =>
      new Set(
        [...realUserOptions, ...sharedEmailOptions].map((o) => String(o.value))
      ),
    [realUserOptions, sharedEmailOptions]
  );

  return {
    fromOptions,
    /** Currently-selectable sender values; excludes a saved-but-stale sender. */
    validFromValues,
    /** Async From options. */
    fromSelectProps,
    /** First page of active users; also labels saved chips. */
    ccBccOptions,
    /** Async CC/BCC options. */
    ccBccSelectProps,
    /** id → email for any recipient seen so far. */
    getCcBccEmailByValue,
    /** `true` until the delegatable-senders fetch settles. Callers
     *  that default `from` to the current user should wait for this
     *  to flip before matching the id against `fromOptions`. */
    isFromOptionsLoading: isLoading,
    isFromOptionsFetched: isFetched,
  };
}

/**
 * `true` when the form's `from` value identifies a shared mailbox
 * (set via `useScheduleSenderOptions`'s `SHARED_EMAIL_VALUE_PREFIX`).
 */
export function isSharedEmailFromValue(
  rawFrom: string | null | undefined
): boolean {
  return (
    typeof rawFrom === 'string' && rawFrom.startsWith(SHARED_EMAIL_VALUE_PREFIX)
  );
}

/**
 * Extract the numeric id portion of a `shared:${id}` form value.
 * Returns `undefined` for malformed input (defensive — should never
 * happen with options produced by `useScheduleSenderOptions`).
 */
export function parseSharedEmailId(
  rawFrom: string | null | undefined
): number | undefined {
  if (!isSharedEmailFromValue(rawFrom)) return undefined;
  const id = Number(
    (rawFrom as string).slice(SHARED_EMAIL_VALUE_PREFIX.length)
  );
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

/**
 * What `from` defaults to: the scheduler, whoever they are.
 *
 * It used to resolve to `''` when they were not in the delegatable list, and
 * the steps then fell back to `fromOptions[0]` — so a recruiter whose own
 * mailbox is not connected either saw an empty required field or, worse, sent
 * the email under whichever colleague sorted first, with nothing on screen
 * saying so.
 *
 * Neither is needed now: the sender check names them and the reason on the
 * field itself (ENG-3916), and `senderPickerOption` keeps them labelled in
 * the picker. Seeding them and explaining beats substituting or blanking.
 *
 * Callers must still wait for `isFromOptionsFetched` before writing this —
 * seeding ahead of the options makes the cleanup pass race the seed.
 */
export function defaultFromValueForCurrentUser(
  currentUserId: number | string | null | undefined
): string {
  if (currentUserId === null || currentUserId === undefined) return '';
  return String(currentUserId);
}

/** Anyone the From picker has to render but may not stock. */
export interface SenderLike {
  id?: number | string | null;
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
}

/**
 * A sender as a From option, so the picker can still label them when they are
 * not delegatable — the scheduler (always seeded) or a saved sender the
 * interview was booked with. Merged as a KNOWN option, never into
 * `validFromValues`: being visible is not being valid.
 *
 * Returns an array so callers can spread it — an unlabelled sender contributes
 * nothing rather than an empty row.
 */
export function senderPickerOption(
  user: SenderLike | null | undefined
): SelectOption[] {
  if (user?.id === undefined || user?.id === null || user.id === '') return [];
  const label = user.full_name?.trim() || user.name?.trim() || user.email;
  if (!label) return [];
  return [
    {
      value: String(user.id),
      label,
      description: user.email ?? undefined,
      image: user.avatar_url ?? undefined,
    },
  ];
}
